/**
 * Utility functions for handling Romanian CUI (Company Unique Identifier) processing
 * Handles RO prefix detection, cleaning, and VAT status determination
 */

export interface CUIProcessingResult {
  originalInput: string;
  cleanCui: string;
  hasVatRegistration: boolean;
  isValid: boolean;
  error?: string;
}

/**
 * Detects if CUI input has "RO" prefix indicating VAT registration
 * @param cuiInput - Raw CUI input from user
 * @returns boolean indicating if RO prefix is present
 */
export const hasRoPrefix = (cuiInput: string): boolean => {
  const trimmed = cuiInput.trim().toUpperCase();
  return trimmed.startsWith("RO");
};

/**
 * Removes RO prefix and cleans CUI for API calls
 * @param cuiInput - Raw CUI input from user
 * @returns Cleaned CUI (digits only, no RO prefix)
 */
export const cleanCui = (cuiInput: string): string => {
  let cleaned = cuiInput.trim().toUpperCase();

  // Remove RO prefix if present
  if (cleaned.startsWith("RO")) {
    cleaned = cleaned.substring(2);
  }

  // Remove any remaining whitespace
  cleaned = cleaned.trim();

  return cleaned;
};

/**
 * Validates Romanian CUI format (7 or 8 digits)
 * @param cui - CUI to validate (should be cleaned)
 * @returns boolean indicating if CUI format is valid
 */
export const validateCuiFormat = (cui: string): boolean => {
  // TODO: Verify if this is correct
  return /^\d{1,8}$/.test(cui);
};

/**
 * Processes raw CUI input and returns all relevant information
 * @param cuiInput - Raw CUI input from user
 * @returns CUIProcessingResult with all processing information
 */
export const processCuiInput = (cuiInput: string): CUIProcessingResult => {
  if (!cuiInput || cuiInput.trim().length === 0) {
    return {
      originalInput: cuiInput,
      cleanCui: "",
      hasVatRegistration: false,
      isValid: false,
      error: "CUI nu poate fi gol",
    };
  }

  const originalInput = cuiInput.trim();
  const hasVat = hasRoPrefix(originalInput);
  const cleaned = cleanCui(originalInput);
  const isValid = validateCuiFormat(cleaned);

  const result: CUIProcessingResult = {
    originalInput,
    cleanCui: cleaned,
    hasVatRegistration: hasVat,
    isValid,
  };

  if (!isValid) {
    result.error = "CUI trebuie să conțină 7 sau 8 cifre";
  }

  return result;
};

/**
 * Formats CUI for display purposes
 * @param cui - CUI to format
 * @param includeRo - Whether to include RO prefix for VAT registered companies
 * @returns Formatted CUI string
 */
export const formatCuiForDisplay = (
  cui: string,
  includeRo: boolean = false
): string => {
  const cleaned = cleanCui(cui);
  return includeRo ? `RO${cleaned}` : cleaned;
};
